import { useEffect, useState, type FC } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useLazyQuery, useMutation } from "@apollo/client"

import KnownIssueDaySummary from "./KnownIssueDaySummary"
import styles from "./KnownIssueDetailPage.module.scss"

import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import { formatJstDateUntilMinute } from "@/utils/date"

const GetKnownIssue = graphql(/* GraphQL */ `
	query GetKnownIssue($id: String!) {
		knownIssue(id: $id) {
			knownIssue {
				id
				status
				description
				issueType
				logLevel
				logMatchMethod
				logKeyword
				logModuleName
				logKind
				logSalonIds
				slackChannelNameToNotify
				mentionTargets {
					slackMentionTargetId
					name
				}
				thresholdCountToNotify
				cooldownHoursToNotify
				autoResolveDaysAfterLastActivity
				shouldSendIosCriticalNotification
				registerTime
				issueModifiedTime
				latestAlertedTime
				opensearchQueryUrl
			}
		}
	}
`)

const GetKnownIssueDaySummary = graphql(/* GraphQL */ `
	query GetKnownIssueDaySummary($id: String!) {
		knownIssueDaySummary(id: $id) {
			daySummaries {
				...KnownIssueDaySummaryFragment
			}
		}
	}
`)

const ResolveKnownIssues = graphql(/* GraphQL */ `
	mutation ResolveKnownIssueDetail($input: ResolveKnownIssuesInput!) {
		resolveKnownIssues(input: $input) {
			ids
		}
	}
`)

const CloseKnownIssues = graphql(/* GraphQL */ `
	mutation CloseKnownIssueDetail($input: CloseKnownIssuesInput!) {
		closeKnownIssues(input: $input) {
			ids
		}
	}
`)

const ReopenKnownIssues = graphql(/* GraphQL */ `
	mutation ReopenKnownIssueDetail($input: ReopenKnownIssuesInput!) {
		reopenKnownIssues(input: $input) {
			ids
		}
	}
`)

const statusLabels: Record<string, string> = {
	OPEN: "Open",
	MANUALLY_RESOLVED: "Manually Resolved",
	AUTO_RESOLVED: "Auto Resolved",
	CLOSED: "Closed",
}

const KnownIssueDetailPage: FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const [confirmAction, setConfirmAction] = useState<
		"resolve" | "close" | "reopen" | null
	>(null)

	const [fetchKnownIssue, { data, loading, error }] =
		useLazyQuery(GetKnownIssue)
	const [
		fetchDaySummary,
		{ data: daySummaryData, loading: daySummaryLoading },
	] = useLazyQuery(GetKnownIssueDaySummary)

	const [resolveKnownIssues, { loading: resolveLoading }] =
		useMutation(ResolveKnownIssues)
	const [closeKnownIssues, { loading: closeLoading }] =
		useMutation(CloseKnownIssues)
	const [reopenKnownIssues, { loading: reopenLoading }] =
		useMutation(ReopenKnownIssues)

	const isActionLoading = resolveLoading || closeLoading || reopenLoading

	useEffect(() => {
		if (id) {
			fetchKnownIssue({ variables: { id }, fetchPolicy: "network-only" })
			fetchDaySummary({ variables: { id }, fetchPolicy: "network-only" })
		}
	}, [id, fetchKnownIssue, fetchDaySummary])

	const issue = data?.knownIssue.knownIssue

	const handleResolve = () => setConfirmAction("resolve")
	const handleClose = () => setConfirmAction("close")
	const handleReopen = () => setConfirmAction("reopen")
	const handleCancel = () => setConfirmAction(null)

	const handleConfirm = async () => {
		if (!id || !confirmAction) return

		try {
			if (confirmAction === "resolve") {
				await resolveKnownIssues({ variables: { input: { ids: [id] } } })
			} else if (confirmAction === "close") {
				await closeKnownIssues({ variables: { input: { ids: [id] } } })
			} else if (confirmAction === "reopen") {
				await reopenKnownIssues({ variables: { input: { ids: [id] } } })
			}
			setConfirmAction(null)
			// Refetch to update status
			await fetchKnownIssue({ variables: { id }, fetchPolicy: "network-only" })
		} catch (err) {
			console.error("Action failed:", err)
			alert("操作に失敗しました")
		}
	}

	// loading または初回レンダリング時（data も error もない）
	if (loading || (!data && !error)) {
		return (
			<div className={styles.container}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	if (error || !issue) {
		return (
			<div className={styles.container}>
				<div className={styles.error}>
					{error?.message || "Known Issue が見つかりません"}
				</div>
				<button
					className={styles.backButton}
					onClick={() => navigate("/souhl_admin/known_issues")}
				>
					一覧に戻る
				</button>
			</div>
		)
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>Known Issue 詳細</h1>
				<div className={styles.actions}>
					<button
						className={styles.backButton}
						onClick={() => navigate("/souhl_admin/known_issues")}
					>
						一覧に戻る
					</button>
					{issue.status === "OPEN" && (
						<>
							<button
								className={styles.resolveButton}
								onClick={handleResolve}
								disabled={isActionLoading}
							>
								Resolve
							</button>
							<button
								className={styles.closeButton}
								onClick={handleClose}
								disabled={isActionLoading}
							>
								Close
							</button>
						</>
					)}
					{(issue.status === "MANUALLY_RESOLVED" ||
						issue.status === "AUTOMATICALLY_RESOLVED") && (
						<button
							className={styles.reopenButton}
							onClick={handleReopen}
							disabled={isActionLoading}
						>
							Open に戻す
						</button>
					)}
					<button
						className={styles.editButton}
						onClick={() => navigate(`/souhl_admin/known_issues/${id}/edit`)}
					>
						編集
					</button>
				</div>
			</div>

			<div className={styles.content}>
				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>基本情報</h2>
					<div className={styles.grid}>
						<div className={styles.field}>
							<span className={styles.label}>ID</span>
							<span className={styles.value}>{issue.id}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Status</span>
							<span
								className={`${styles.value} ${styles.status} ${styles[`status${issue.status}`]}`}
							>
								{statusLabels[issue.status] || issue.status}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Issue Type</span>
							<span className={styles.value}>{issue.issueType}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Description</span>
							<span className={styles.value}>{issue.description || "-"}</span>
						</div>
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>ログマッチング設定</h2>
					<div className={styles.grid}>
						<div className={styles.field}>
							<span className={styles.label}>Log Level</span>
							<span className={`${styles.value} ${styles.logLevel}`}>
								{issue.logLevel}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Match Method</span>
							<span className={styles.value}>{issue.logMatchMethod}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Log Keyword</span>
							<span className={styles.value}>{issue.logKeyword}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Log Module Name</span>
							<span className={styles.value}>{issue.logModuleName}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Log Kind</span>
							<span className={styles.value}>{issue.logKind || "-"}</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Log Salon IDs</span>
							<span className={styles.value}>
								{issue.logSalonIds.length > 0
									? issue.logSalonIds.join(", ")
									: "-"}
							</span>
						</div>
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>通知設定</h2>
					<div className={styles.grid}>
						<div className={styles.field}>
							<span className={styles.label}>Slack Channel</span>
							<span className={styles.value}>
								{issue.slackChannelNameToNotify}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Mention Targets</span>
							<span className={styles.value}>
								{issue.mentionTargets.length > 0
									? issue.mentionTargets.map((t) => t.name).join(", ")
									: "-"}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Threshold Count</span>
							<span className={styles.value}>
								{issue.thresholdCountToNotify}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>Cooldown Hours</span>
							<span className={styles.value}>
								{issue.cooldownHoursToNotify != null
									? `${issue.cooldownHoursToNotify} 時間`
									: "なし（常に通知）"}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>
								最終アクティビティ(更新orアラート)から自動解決までの日数
							</span>
							<span className={styles.value}>
								{issue.autoResolveDaysAfterLastActivity} 日
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>iOS Critical Notification</span>
							<span className={styles.value}>
								{issue.shouldSendIosCriticalNotification ? "有効" : "無効"}
							</span>
						</div>
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>タイムスタンプ</h2>
					<div className={styles.grid}>
						<div className={styles.field}>
							<span className={styles.label}>登録日時</span>
							<span className={styles.value}>
								{formatJstDateUntilMinute(new Date(issue.registerTime))}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>更新日時</span>
							<span className={styles.value}>
								{formatJstDateUntilMinute(new Date(issue.issueModifiedTime))}
							</span>
						</div>
						<div className={styles.field}>
							<span className={styles.label}>最終アラート日時</span>
							<span className={styles.value}>
								{issue.latestAlertedTime
									? formatJstDateUntilMinute(new Date(issue.latestAlertedTime))
									: "-"}
							</span>
						</div>
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>7日間サマリー</h2>
					{daySummaryLoading ? (
						<div className={styles.loading}>読み込み中...</div>
					) : daySummaryData?.knownIssueDaySummary.daySummaries ? (
						<KnownIssueDaySummary
							daySummaries={daySummaryData.knownIssueDaySummary.daySummaries}
						/>
					) : (
						<div>-</div>
					)}
				</section>

				{issue.opensearchQueryUrl && (
					<section className={styles.section}>
						<h2 className={styles.sectionTitle}>OpenSearch</h2>
						<a
							href={issue.opensearchQueryUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.link}
						>
							OpenSearch でログを確認
						</a>
					</section>
				)}
			</div>

			{confirmAction && (
				<div className={styles.dialogOverlay}>
					<div className={styles.dialog}>
						<h3 className={styles.dialogTitle}>確認</h3>
						<p className={styles.dialogMessage}>
							この Known Issue を
							{confirmAction === "resolve"
								? " Resolve "
								: confirmAction === "close"
									? " Close "
									: " Open に戻す "}
							しますか？
						</p>
						<div className={styles.dialogActions}>
							<button
								className={styles.cancelButton}
								onClick={handleCancel}
								disabled={isActionLoading}
							>
								キャンセル
							</button>
							<button
								className={styles.confirmButton}
								onClick={handleConfirm}
								disabled={isActionLoading}
							>
								{isActionLoading ? "処理中..." : "OK"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default KnownIssueDetailPage
